/**
 * aggReportItemRenderer.ts
 *
 * Renders Stats > Aggregate report items (frequency table, cross-tabulation, bar/line graph) to
 * self-contained HTML strings for display in a WebView — the results-card equivalent of
 * chartRenderer.ts, and a direct sibling to it (reuses its renderChart for Bar/Line). Same
 * conventions as chartRenderer.ts: pure functions, no external dependencies, never throws (a
 * missing/malformed input produces a visible inline placeholder or error block instead of
 * blanking the whole results card), inline-styled output with no external CSS/script
 * dependency.
 */

import { renderChart, type ChartConfig, type ChartSeries } from './chartRenderer';
import type { StatsAggDataPointDto, StatsAggItemResultDto, StatsAggReportItemDefDto } from '../types/api/stats';

const FONT = 'Arial, sans-serif';

const escapeHtml = (s: string): string =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const itemTitleHtml = (title?: string): string =>
  title
    ? `<h3 style="margin:0 0 8px;font-size:14px;font-weight:bold;color:#2c3e50;font-family:${FONT};text-align:center;">${escapeHtml(title)}</h3>`
    : '';

const itemErrorHtml = (message: string): string =>
  `<div style="text-align:center;margin:12px 0;"><p style="color:#e74c3c;font-family:${FONT};font-size:13px;border:1px solid #e74c3c;border-radius:6px;padding:10px;display:inline-block;">Report item error: ${escapeHtml(message)}</p></div>`;

const itemEmptyHtml = (): string =>
  `<div style="text-align:center;margin:12px 0;"><p style="color:#7f8c8d;font-family:${FONT};font-size:13px;">No data.</p></div>`;

/** Preserves first-seen order rather than sorting, so category order matches the order the
 * backend returned data points in (typically the order set by whatever produced the aggregate). */
const uniqueInOrder = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
};

export interface FrequencyTableConfig {
  title?: string;
  rows: { label: string; value: string }[];
}

/** Simple two-column frequency table — one row per category/value. */
export function renderFrequencyTable(config: FrequencyTableConfig): string {
  const rowsHtml = config.rows
    .map(
      (row) => `<tr>
        <td style="padding:6px 12px;border-bottom:1px solid #e5ebf3;color:#334155;font-family:${FONT};font-size:13px;">${escapeHtml(row.label)}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #e5ebf3;color:#0f172a;font-family:${FONT};font-size:13px;text-align:right;">${escapeHtml(row.value)}</td>
      </tr>`
    )
    .join('');

  return `<div style="margin:12px 0;">
${itemTitleHtml(config.title)}
<table style="width:100%;max-width:420px;margin:0 auto;border-collapse:collapse;">${rowsHtml}</table>
</div>`;
}

export interface CrossTabConfig {
  title?: string;
  rowLabels: string[];
  colLabels: string[];
  /** matrix[r][c] — a cell value, keyed by rowLabels[r]/colLabels[c]. */
  matrix: string[][];
}

/** HTML table with a blank corner cell, one column header per colLabels entry, and one row
 * header per rowLabels entry. */
export function renderCrossTab(config: CrossTabConfig): string {
  const headerCells = config.colLabels
    .map((col) => `<th style="padding:6px 12px;border-bottom:2px solid #cbd5e1;color:#334155;font-family:${FONT};font-size:13px;text-align:right;">${escapeHtml(col)}</th>`)
    .join('');

  const bodyRows = config.rowLabels
    .map((row, r) => {
      const cells = config.colLabels
        .map((_, c) => {
          const value = config.matrix[r]?.[c] ?? '';
          return `<td style="padding:6px 12px;border-bottom:1px solid #e5ebf3;color:#0f172a;font-family:${FONT};font-size:13px;text-align:right;">${escapeHtml(value)}</td>`;
        })
        .join('');
      return `<tr>
        <th style="padding:6px 12px;border-bottom:1px solid #e5ebf3;color:#334155;font-family:${FONT};font-size:13px;text-align:left;">${escapeHtml(row)}</th>
        ${cells}
      </tr>`;
    })
    .join('');

  return `<div style="margin:12px 0;overflow-x:auto;">
${itemTitleHtml(config.title)}
<table style="border-collapse:collapse;margin:0 auto;">
<tr><th style="padding:6px 12px;border-bottom:2px solid #cbd5e1;"></th>${headerCells}</tr>
${bodyRows}
</table>
</div>`;
}

/**
 * Builds one report item's rendered HTML block from its definition and matching run result,
 * dispatching to the renderer for its Type. Never throws — a missing result, empty data, or
 * unknown Type all degrade to a visible placeholder/error rather than blanking the whole card.
 */
export function buildAggItemHtml(
  itemDef: StatsAggReportItemDefDto,
  result: StatsAggItemResultDto | undefined
): string {
  try {
    const data: StatsAggDataPointDto[] = result?.Data ?? [];
    if (data.length === 0) {
      return `${itemTitleHtml(itemDef.DName)}${itemEmptyHtml()}`;
    }

    switch (itemDef.Type) {
      case 'Frequency': {
        return renderFrequencyTable({
          title: itemDef.DName,
          rows: data.map((d) => ({ label: d.Label, value: d.Value })),
        });
      }
      case 'CrossTab': {
        const rowLabels = uniqueInOrder(data.map((d) => d.Label));
        const colLabels = uniqueInOrder(data.map((d) => d.GroupLabel));
        const matrix = rowLabels.map((rowLabel) =>
          colLabels.map((colLabel) => {
            const point = data.find((d) => d.Label === rowLabel && d.GroupLabel === colLabel);
            return point?.Value ?? '';
          })
        );
        return renderCrossTab({ title: itemDef.DName, rowLabels, colLabels, matrix });
      }
      case 'Bar': {
        const labels = uniqueInOrder(data.map((d) => d.Label));
        const values = labels.map((label) => data.find((d) => d.Label === label)?.Value ?? '0');
        const config: ChartConfig = { type: 'bar', title: itemDef.DName, labels, values };
        return renderChart(config);
      }
      case 'Line': {
        const labels = uniqueInOrder(data.map((d) => d.Label));
        const groupLabels = uniqueInOrder(data.map((d) => d.GroupLabel));
        // No GroupLabel on any point ⇒ one implicit, unlabeled series (matches Bar's shape).
        const series: ChartSeries[] = (groupLabels.length === 1 && groupLabels[0] === '')
          ? [{ values: labels.map((label) => data.find((d) => d.Label === label)?.Value ?? '0') }]
          : groupLabels.map((group) => ({
              label: group,
              values: labels.map((label) => data.find((d) => d.Label === label && d.GroupLabel === group)?.Value ?? '0'),
            }));
        const config: ChartConfig = { type: 'line', title: itemDef.DName, labels, series };
        return renderChart(config);
      }
      default:
        return itemErrorHtml(`unknown report item type "${itemDef.Type}".`);
    }
  } catch (error) {
    return itemErrorHtml(error instanceof Error ? error.message : String(error));
  }
}

const parsePos = (pos: string): number => {
  const parsed = parseInt(pos, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

/**
 * Renders every item in a report, in Pos order, concatenated into one HTML string with a
 * visual spacer between items — ready to pass to wrapReportHtml() for display in a WebView.
 */
export function renderAggReportItems(
  itemDefs: StatsAggReportItemDefDto[],
  results: StatsAggItemResultDto[]
): string {
  const resultByItemId = new Map(results.map((r) => [r.ItemId, r]));
  const sortedItems = [...itemDefs].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));

  if (sortedItems.length === 0) {
    return `<div style="text-align:center;margin:24px 0;"><p style="color:#7f8c8d;font-family:${FONT};font-size:13px;">This report has no items configured yet.</p></div>`;
  }

  return sortedItems
    .map((itemDef) => buildAggItemHtml(itemDef, resultByItemId.get(itemDef.ItemId)))
    .join('<div style="height:1px;background:#e5ebf3;margin:16px 0;"></div>');
}
